import type { FactureAddonResume, SouscriptionAddonResume } from '@artosera/shared';
import { ApiError, modeDemo, requete } from './api.js';
export interface VenteAddon {
  id: string;
  galerieId: string;
  galerieNom: string | null;
  addon?: string;
  nature?: string;
  formule?: string;
  htNetCentimes?: number;
  depuisLe?: string;
  jusquAu?: string | null;
  finPeriode?: string;
  factureId?: string;
  numero?: string;
  echeance?: string;
  etat?: string;
  raisonSuspension?: SouscriptionAddonResume['raisonSuspension'];
  renouvellement?: 'auto' | 'fin_de_periode';
  revision?: number;
  ttcCentimes?: number;
  regleCentimes?: number;
  estTest: boolean;
}
export interface VentesAddonsVue {
  indicateurs: {
    revenuMensuelHtCentimes: number;
    essaisEnCours: number;
    essaisClos: number;
    essaisConvertis: number;
    tauxConversionPointsDeBase: number;
    facturesEnRetard: number;
    retardTtcCentimes: number;
  };
  espacesTest: number;
  items: VenteAddon[];
  total: number;
  page: number;
  taillePage: number;
}
export interface FactureAddonDetail extends FactureAddonResume {
  avoirs: FactureAddonResume[];
  revision: number;
  galerieId: string;
  destinataire: { nom: string };
  lignes: { designation: string; quantite: number; prixUnitaireHtCentimes: number }[];
  totaux: { totalLignesCentimes: number; totalTvaCentimes: number; totalTtcCentimes: number };
  tva: { regime: string };
  encaissements: {
    id: string;
    encaisseLe: string;
    montantTtcCentimes: number;
    referenceBancaire: string;
  }[];
  remboursements: {
    rembourseLe: string;
    montantTtcCentimes: number;
    referenceBancaire: string;
    motif: string;
    auteurId: string;
    auteurEmail: string;
  }[];
  paiementsEnLigne: { paiementId: string; montantCentimes: number }[];
  remboursementsEnLigne: {
    _id: string;
    paiementId: string;
    montantTtcCentimes: number;
    statut: 'demande' | 'confirme' | 'echoue';
    refundId: string | null;
  }[];
  creance: { etat: string; encaisseCentimes: number } | null;
  envoi: 'a_envoyer' | 'en_cours' | 'envoye' | 'echec';
}
function exigerReel() {
  if (modeDemo()) throw new ApiError(403, 'acces.refuse', 'Indisponible en démonstration.');
}
export type SaisieAvoir = { nature: 'total' } | { nature: 'partiel'; montantHtCentimes: number };
export const ventesAddonsApi = {
  suspension: (
    id: string,
    geste: 'suspendre' | 'reactiver',
    corps: { revision: number; motif: string },
  ) => {
    exigerReel();
    return requete(`/v1/console/addons/souscriptions/${encodeURIComponent(id)}/${geste}`, {
      methode: 'POST',
      corps,
    });
  },
  devisAvoir: (
    id: string,
    saisie: SaisieAvoir,
  ): Promise<{ totaux: { totalTtcCentimes: number }; revisionFacture: number }> => {
    exigerReel();
    const params = new URLSearchParams({ nature: saisie.nature });
    if (saisie.nature === 'partiel')
      params.set('montantHtCentimes', String(saisie.montantHtCentimes));
    return requete(`/v1/console/addons/factures/${id}/avoirs/devis?${params}`);
  },
  avoir: (id: string, corps: SaisieAvoir & { motif: string; revisionFacture: number }) => {
    exigerReel();
    return requete(`/v1/console/addons/factures/${id}/avoirs`, { methode: 'POST', corps });
  },
  souscriptions: (galerie: string): Promise<{ items: SouscriptionAddonResume[] }> => {
    exigerReel();
    return requete(`/v1/console/galleries/${encodeURIComponent(galerie)}/addons/souscriptions`);
  },
  resiliation: (
    galerie: string,
    id: string,
    geste: 'resilier' | 'annuler-resiliation',
    corps: { revision: number; motif: string },
  ) => {
    exigerReel();
    return requete(
      `/v1/console/galleries/${encodeURIComponent(galerie)}/addons/souscriptions/${encodeURIComponent(id)}/${geste}`,
      { methode: 'POST', corps },
    );
  },
  ventes: (params: URLSearchParams): Promise<VentesAddonsVue> => {
    exigerReel();
    return requete(`/v1/console/addons/ventes?${params}`);
  },
  facture: (id: string): Promise<FactureAddonDetail> => {
    exigerReel();
    return requete(`/v1/console/addons/factures/${id}`);
  },
  encaisser: (
    id: string,
    corps: {
      encaisseLe: string;
      montantTtcCentimes: number;
      referenceBancaire: string;
      revisionFacture: number;
    },
  ) => {
    exigerReel();
    return requete(`/v1/console/addons/factures/${id}/encaissements`, { methode: 'POST', corps });
  },
  annuler: (id: string, encaissementId: string, motif: string) => {
    exigerReel();
    return requete(`/v1/console/addons/factures/${id}/encaissements/${encaissementId}`, {
      methode: 'DELETE',
      corps: { motif },
    });
  },
  rembourserEnLigne: (
    id: string,
    corps: {
      paiementId: string;
      montantTtcCentimes: number;
      motif: string;
      revisionFacture: number;
    },
  ) => {
    exigerReel();
    return requete(`/v1/console/addons/factures/${id}/remboursements-en-ligne`, {
      methode: 'POST',
      corps,
    });
  },
  rembourser: (
    id: string,
    corps: {
      rembourseLe: string;
      montantTtcCentimes: number;
      referenceBancaire: string;
      motif: string;
      revisionFacture: number;
    },
  ) => {
    exigerReel();
    return requete(`/v1/console/addons/factures/${id}/remboursement`, { methode: 'POST', corps });
  },
  renvoyer: (id: string, motif: string) => {
    exigerReel();
    return requete(`/v1/console/addons/factures/${id}/renvoyer`, {
      methode: 'POST',
      corps: { motif },
    });
  },
};
